/**
 * Backtesting.
 *
 * ## The screen is built around what a backtest cannot tell you
 *
 * Every backtesting tool shows an equity curve and a win rate. The ones worth
 * trusting also show what the number rests on, and this screen puts three of
 * those beside the result rather than in a help article:
 *
 *   * **Where the data came from.** A run on generated bars exercises the
 *     engine and says nothing about the strategy. The caveat is written by the
 *     API so no client can forget it.
 *   * **How many bars were ambiguous.** When a bar's range contains both the
 *     stop and the target, the outcome depends on a path the bar does not
 *     record. Resolved as losses and counted — a result resting on fifty
 *     coin-flips should look different from one that never faced the question.
 *   * **What it cost.** Commission and slippage are shown as their own line,
 *     because a backtest without them flatters exactly the strategies that
 *     trade most.
 *
 * ## And the comparison, which is the point
 *
 * A backtest inside a journal can do what a backtesting tool cannot: put the
 * hypothetical record beside the real one for the same strategy. AEGIS shows
 * the gap and does not explain it — slippage, discipline and a dead edge look
 * identical from here, and guessing between them would be advice.
 */
import { useCallback, useEffect, useState } from 'react';
import type { JSX } from 'react';
import type {
  BacktestCapabilities, BacktestComparison, BacktestDto, BacktestRules, StrategyDto,
} from '@aegis/api-client';
import { ApiError } from '@aegis/api-client';
import { api } from '../lib/api.js';
import { EmptyState } from '../components/EmptyState.js';
import { Link } from '../lib/router.js';

const money = (s: string): string => {
  const n = Number(s);
  return `${n < 0 ? '−' : n > 0 ? '+' : ''}$${Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
};
const pct = (s: string): string => `${(Number(s) * 100).toFixed(1)}%`;
const day = (iso: string): string => iso.slice(0, 10);

/**
 * The starting rules.
 *
 * A moving-average cross is chosen because it is the most familiar rule in
 * trading and therefore the clearest illustration of the language — not because
 * it is a good strategy. Nothing in the product suggests it is.
 */
const DEFAULT_RULES: BacktestRules = {
  direction: 'long',
  entry: {
    kind: 'crossesAbove',
    left: { kind: 'price', field: 'close' },
    right: { kind: 'sma', length: 20 },
  },
  exit: { stopAtr: 1.5, targetAtr: 3 },
  quantity: 1,
  maxConcurrent: 1,
};

const SYMBOLS = ['ES', 'NQ', 'YM', 'RTY', 'CL', 'NG', 'MES', 'MNQ'];

export function BacktestScreen(): JSX.Element {
  const [caps, setCaps] = useState<BacktestCapabilities | null>(null);
  const [runs, setRuns] = useState<BacktestDto[]>([]);
  const [strategies, setStrategies] = useState<StrategyDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [comparison, setComparison] = useState<BacktestComparison | null>(null);

  const [symbol, setSymbol] = useState('ES');
  const [timeframe, setTimeframe] = useState('5m');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [strategyId, setStrategyId] = useState('');
  const [maLength, setMaLength] = useState(20);
  const [direction, setDirection] = useState<'long' | 'short'>('long');
  const [stopAtr, setStopAtr] = useState(1.5);
  const [targetAtr, setTargetAtr] = useState(3);
  const [slippageTicks, setSlippageTicks] = useState(1);
  const [commission, setCommission] = useState('2.50');

  const load = useCallback(async () => {
    try {
      const [c, r, s] = await Promise.all([
        api.backtestCapabilities(symbol), api.backtests(), api.strategies(),
      ]);
      setCaps(c);
      setRuns(r.backtests);
      setStrategies(s.strategies);

      // Default the range to the last month the source can actually serve, so
      // the first run works instead of returning "no bars in that range".
      const earliest = c.earliest[timeframe];
      if (earliest && !from) {
        const end = new Date();
        const start = new Date(Math.max(
          new Date(earliest).getTime(), end.getTime() - 30 * 86_400_000,
        ));
        setFrom(start.toISOString().slice(0, 10));
        setTo(end.toISOString().slice(0, 10));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load backtesting.');
    }
  }, [symbol, timeframe, from]);

  useEffect(() => { void load(); }, [load]);

  const rules = (): BacktestRules => {
    const cross = {
      kind: direction === 'long' ? ('crossesAbove' as const) : ('crossesBelow' as const),
      left: { kind: 'price' as const, field: 'close' as const },
      right: { kind: 'sma' as const, length: maLength },
    };
    return {
      ...DEFAULT_RULES,
      direction,
      entry: direction === 'long' ? cross : DEFAULT_RULES.entry,
      ...(direction === 'short' ? { shortEntry: cross } : {}),
      exit: { stopAtr, targetAtr },
    };
  };

  const run = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setComparison(null);
    try {
      const made = await api.runBacktest({
        name: `${direction === 'long' ? 'Cross above' : 'Cross below'} SMA ${maLength}`,
        strategyId: strategyId || null,
        symbol, timeframe, from, to,
        rules: rules(),
        costs: {
          commissionPerContract: commission,
          feesPerContract: '1.50',
          slippageTicks,
        },
      });
      setRuns((rs) => [made, ...rs]);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The backtest could not run.');
    } finally { setBusy(false); }
  };

  const compare = async (id: string): Promise<void> => {
    setError(null);
    try { setComparison(await api.compareBacktest(id)); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Could not compare.'); }
  };

  const remove = async (id: string): Promise<void> => {
    await api.deleteBacktest(id);
    setRuns((rs) => rs.filter((r) => r.id !== id));
    if (comparison?.backtest.id === id) setComparison(null);
  };

  if (!caps) {
    return error
      ? <div className="notice error">{error}</div>
      : <div className="dim">Loading…</div>;
  }

  const earliest = caps.earliest[timeframe];

  return (
    <div className="scroll-area">
      <div className="section-head">
        <h2>Backtesting</h2>
        <span className="dim">
          Rules over historical bars, scored by the same maths as your journal.
        </span>
      </div>

      {/* The most important sentence on the screen, and the API wrote it. */}
      <div className={`notice ${caps.sourceId === 'mock' ? 'error' : ''}`}>{caps.dataNote}</div>

      {!caps.entitled && (
        <div className="notice">
          Backtesting is part of the paid plans.{' '}
          <Link to="/pricing">See what changes</Link>.
        </div>
      )}

      {error && <div className="notice error">{error}</div>}

      <div className="report-controls">
        <label>
          <span>Instrument</span>
          <select value={symbol} onChange={(e) => setSymbol(e.target.value)}>
            {SYMBOLS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label>
          <span>Timeframe</span>
          <select value={timeframe} onChange={(e) => setTimeframe(e.target.value)}>
            {caps.timeframes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label>
          <span>From</span>
          <input type="date" value={from} min={earliest ? day(earliest) : undefined}
            onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label>
          <span>To</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
      </div>

      <div className="report-controls">
        <label>
          <span>Entry</span>
          <select value={direction} onChange={(e) => setDirection(e.target.value as 'long' | 'short')}>
            <option value="long">Close crosses above the average</option>
            <option value="short">Close crosses below the average</option>
          </select>
        </label>
        <label>
          <span>Average length</span>
          <input type="number" min={2} max={500} value={maLength}
            onChange={(e) => setMaLength(Math.max(2, Number(e.target.value) || 20))} />
        </label>
        <label>
          <span>Stop (× ATR)</span>
          <input type="number" min={0.1} step={0.1} value={stopAtr}
            onChange={(e) => setStopAtr(Number(e.target.value) || 1.5)} />
        </label>
        <label>
          <span>Target (× ATR)</span>
          <input type="number" min={0.1} step={0.1} value={targetAtr}
            onChange={(e) => setTargetAtr(Number(e.target.value) || 3)} />
        </label>
        <label>
          <span>Slippage (ticks)</span>
          <input type="number" min={0} max={20} value={slippageTicks}
            onChange={(e) => setSlippageTicks(Number(e.target.value) || 0)} />
        </label>
        <label>
          <span>Commission per contract</span>
          <input type="text" inputMode="decimal" value={commission}
            onChange={(e) => setCommission(e.target.value)} />
        </label>
        <label>
          <span>Attach to strategy</span>
          <select value={strategyId} onChange={(e) => setStrategyId(e.target.value)}>
            <option value="">— none —</option>
            {strategies.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
      </div>

      <p className="field-hint">
        Attaching a run to a strategy is what lets AEGIS put it beside your real
        trades in that strategy. A stop is required — without one the worst case
        is unbounded and the equity curve describes a position nobody would hold.
      </p>

      <button
        className="btn btn-primary"
        disabled={busy || !caps.entitled || !from || !to}
        onClick={() => void run()}
      >
        {busy ? 'Running…' : 'Run backtest'}
      </button>

      {/* ---- comparison ---- */}
      {comparison && (
        <section className="compare">
          <h3>{comparison.backtest.name || 'Backtest'} against your own record</h3>
          <table className="report-table">
            <thead>
              <tr>
                <th />
                <th className="num">Backtest</th>
                <th className="num">Live</th>
                <th className="num">Gap</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Trades</td>
                <td className="num mono">{comparison.backtest.tradeCount}</td>
                <td className="num mono">{comparison.live.tradeCount}</td>
                <td className="num dim">—</td>
              </tr>
              <tr>
                <td>Win rate</td>
                <td className="num mono">{pct(comparison.backtest.winRate)}</td>
                <td className="num mono">{pct(comparison.live.winRate)}</td>
                <td className="num mono">{pct(comparison.gap.winRate)}</td>
              </tr>
              <tr>
                <td>Expectancy</td>
                <td className="num mono">{money(comparison.backtest.expectancy)}</td>
                <td className="num mono">{money(comparison.live.expectancy)}</td>
                <td className="num mono">{money(comparison.gap.expectancy)}</td>
              </tr>
            </tbody>
          </table>
          <p className="field-hint">{comparison.gap.note}</p>
        </section>
      )}

      {/* ---- past runs ---- */}
      <section className="agent-runs">
        <h3>Runs</h3>
        {runs.length === 0 ? (
          <EmptyState icon="target" title="No backtests yet">
            A run is kept as a record of what the engine said, on which data, on
            which day — so re-opening it costs nothing and the answer does not
            move under you.
          </EmptyState>
        ) : (
          <ul className="run-list">
            {runs.map((r) => (
              <li key={r.id} className="run">
                <header>
                  <strong>{r.name || `${r.symbol} ${r.timeframe}`}</strong>
                  <span className="dim">
                    {day(r.from)} → {day(r.to)} · {r.barCount.toLocaleString('en-US')} bars
                  </span>
                </header>

                <div className="bt-stats">
                  <div className="stat">
                    <span className="stat-label">Net</span>
                    <strong className={`mono ${Number(r.netPnl) < 0 ? 'neg' : 'pos'}`}>
                      {money(r.netPnl)}
                    </strong>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Trades</span>
                    <strong className="mono">{r.tradeCount}</strong>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Win rate</span>
                    <strong className="mono">{pct(r.winRate)}</strong>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Max drawdown</span>
                    <strong className="mono">{money(r.maxDrawdown)}</strong>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Costs</span>
                    <strong className="mono">−${Number(r.costsPaid).toFixed(2)}</strong>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Slippage</span>
                    <strong className="mono">−${Number(r.slippagePaid).toFixed(2)}</strong>
                  </div>
                </div>

                {/* The caveats, beside the number rather than under a help link. */}
                <ul className="bt-caveats">
                  <li>
                    <span className="dim">Data</span> {r.sourceId}
                    {r.sourceId === 'mock' && ' — generated, not recorded'}
                  </li>
                  {r.ambiguousBars > 0 && (
                    <li className="warn">
                      <span className="dim">Ambiguous bars</span> {r.ambiguousBars} — the
                      stop and the target were both inside the same bar. Taken as losses,
                      because the bar does not record which came first.
                    </li>
                  )}
                  {r.openAtEnd && (
                    <li className="warn">
                      A position was still open when the data ran out. It is not counted
                      as a result either way.
                    </li>
                  )}
                </ul>

                <div className="strategy-actions">
                  {r.strategyId && (
                    <button className="btn btn-quiet compact" onClick={() => void compare(r.id)}>
                      Compare with my trades
                    </button>
                  )}
                  <button className="btn btn-quiet compact" onClick={() => void remove(r.id)}>
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
